import { format, isAfter, isBefore, isValid, parse } from "date-fns";
import { CalendarIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DatePickerProps {
	id?: string;
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	required?: boolean;
	placeholder?: string;
	className?: string;
	minDate?: string;
	maxDate?: string;
}

function parseDate(value: string): Date | undefined {
	if (!value) return undefined;
	const d = parse(value, "yyyy-MM-dd", new Date());
	return isValid(d) ? d : undefined;
}

const localeUsesMMDD = (() => {
	try {
		const formatted = new Intl.DateTimeFormat(navigator.language, {
			month: "2-digit",
			day: "2-digit",
		}).format(new Date(2000, 0, 15));
		return formatted.indexOf("01") < formatted.indexOf("15");
	} catch {
		return true;
	}
})();

const INPUT_FORMAT = localeUsesMMDD ? "MM/dd/yyyy" : "dd/MM/yyyy";
const DISPLAY_FORMAT = localeUsesMMDD ? "MM/dd/yyyy" : "dd/MM/yyyy";
const PLACEHOLDER = localeUsesMMDD ? "MM/DD/YYYY" : "DD/MM/YYYY";

function formatForDisplay(d: Date): string {
	return format(d, DISPLAY_FORMAT);
}

function formatDigits(digits: string): string {
	let formatted = digits;
	if (digits.length > 2) formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
	if (digits.length > 4)
		formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
	return formatted;
}

function parseInputToDate(formatted: string): Date | undefined {
	const parsed = parse(formatted, INPUT_FORMAT, new Date());
	return isValid(parsed) ? parsed : undefined;
}

export function DatePicker({
	id,
	value,
	onChange,
	disabled = false,
	required = false,
	placeholder = PLACEHOLDER,
	className,
	minDate: minDateStr,
	maxDate: maxDateStr,
}: DatePickerProps) {
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState(() => {
		const d = parseDate(value);
		return d ? formatForDisplay(d) : "";
	});
	const [hasError, setHasError] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const date = parseDate(value);
	const minDate = parseDate(minDateStr ?? "");
	const maxDate = parseDate(maxDateStr ?? "");

	useEffect(() => {
		const d = parseDate(value);
		setInputValue(d ? formatForDisplay(d) : "");
		setHasError(false);
	}, [value]);

	function isDateInRange(d: Date): boolean {
		if (minDate && isBefore(d, minDate)) return false;
		if (maxDate && isAfter(d, maxDate)) return false;
		return true;
	}

	function commitDate(d: Date) {
		if (isDateInRange(d)) {
			onChange(format(d, "yyyy-MM-dd"));
			setHasError(false);
		} else {
			setHasError(true);
		}
	}

	function handleInputChange(text: string) {
		const digits = text.replace(/\D/g, "");
		const formatted = formatDigits(digits);
		setInputValue(formatted);

		if (digits.length === 8) {
			const parsed = parseInputToDate(formatted);
			if (parsed) {
				commitDate(parsed);
			} else {
				setHasError(true);
			}
		} else if (digits.length === 0) {
			onChange("");
			setHasError(false);
		} else {
			setHasError(false);
		}
	}

	function handleInputBlur() {
		const d = parseDate(value);
		setInputValue(d ? formatForDisplay(d) : "");
		if (!d && inputValue.replace(/\D/g, "").length > 0) {
			setHasError(true);
		} else {
			setHasError(false);
		}
	}

	function handleInputKeyDown(e: React.KeyboardEvent) {
		if (e.key === "Escape") {
			setOpen(false);
			inputRef.current?.blur();
		} else if (e.key === "Enter") {
			e.preventDefault();
			const digits = inputValue.replace(/\D/g, "");
			if (digits.length === 8) {
				const parsed = parseInputToDate(formatDigits(digits));
				if (parsed) commitDate(parsed);
			}
			setOpen(false);
		} else if (e.key === "ArrowDown" && !open) {
			e.preventDefault();
			setOpen(true);
		}
	}

	function handleCalendarSelect(day: Date | undefined) {
		if (day) {
			onChange(format(day, "yyyy-MM-dd"));
			setInputValue(formatForDisplay(day));
			setHasError(false);
		} else {
			onChange("");
			setInputValue("");
			setHasError(false);
		}
		setOpen(false);
		inputRef.current?.focus();
	}

	function handleClear() {
		onChange("");
		setInputValue("");
		setHasError(false);
		inputRef.current?.focus();
	}

	const disabledMatcher: Array<{ before?: Date; after?: Date }> = [];
	if (minDate) disabledMatcher.push({ before: minDate });
	if (maxDate) disabledMatcher.push({ after: maxDate });

	const showClear = !disabled && (inputValue.length > 0 || !!value);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<div
				className={cn(
					"flex h-9 w-full items-center rounded-(--radius-sm) border bg-(--color-card) px-3 text-sm shadow-xs ring-offset-(--color-background) focus-within:ring-2 focus-within:ring-offset-2",
					hasError
						? "border-red-500 focus-within:ring-red-500/30"
						: "border-(--color-border) focus-within:ring-(--color-ring)",
					disabled && "cursor-not-allowed opacity-50",
					className,
				)}
			>
				<input
					ref={inputRef}
					id={id}
					type="text"
					inputMode="numeric"
					disabled={disabled}
					required={required}
					placeholder={placeholder}
					value={inputValue}
					onChange={(e) => handleInputChange(e.target.value)}
					onBlur={handleInputBlur}
					onKeyDown={handleInputKeyDown}
					className="flex-1 bg-transparent outline-none placeholder:text-(--color-muted-foreground)"
				/>
				{showClear && (
					<button
						type="button"
						onClick={handleClear}
						tabIndex={-1}
						className="ml-1 shrink-0 text-(--color-muted-foreground) hover:text-(--color-foreground)"
					>
						<XIcon className="size-3.5" />
					</button>
				)}
				<PopoverTrigger asChild>
					<button
						type="button"
						disabled={disabled}
						className="ml-1.5 shrink-0 text-(--color-muted-foreground) hover:text-(--color-foreground)"
					>
						<CalendarIcon className="size-4" />
					</button>
				</PopoverTrigger>
			</div>
			<PopoverContent
				className="w-auto p-0"
				onOpenAutoFocus={(e) => e.preventDefault()}
			>
				<Calendar
					mode="single"
					selected={date}
					onSelect={handleCalendarSelect}
					defaultMonth={date}
					disabled={
						disabledMatcher.length > 0
							? (disabledMatcher as import("react-day-picker").Matcher[])
							: undefined
					}
				/>
			</PopoverContent>
		</Popover>
	);
}
